import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { Suggestion } from "@/hooks/useAgentSuggestions";

interface QuickSuggestionsProps {
  suggestions?: Suggestion[];
  isLoading?: boolean;
  onSelect: (action: string) => void;
  disabled?: boolean;
}

/**
 * Renders role-aware suggestion chips fetched from the backend.
 *
 * Loading state: 4 skeleton pills while fetching.
 * Populated state: one chip per suggestion, sorted by priority from the backend.
 * Accessible chip group with role="group" and aria-label.
 */
export function QuickSuggestions({
  suggestions,
  isLoading,
  onSelect,
  disabled,
}: QuickSuggestionsProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div
        role="group"
        aria-label={t("inspectionChat.suggestionsHeading")}
        className="flex flex-wrap gap-2"
      >
        <div className="h-7 w-24 rounded-full bg-muted animate-pulse" />
        <div className="h-7 w-32 rounded-full bg-muted animate-pulse" />
        <div className="h-7 w-28 rounded-full bg-muted animate-pulse" />
        <div className="h-7 w-36 rounded-full bg-muted animate-pulse" />
      </div>
    );
  }

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div
      role="group"
      aria-label={t("inspectionChat.suggestionsHeading")}
      className="flex flex-wrap gap-2 animate-in fade-in duration-150"
    >
      {suggestions.map((suggestion, idx) => (
        <Button
          key={idx}
          variant="outline"
          size="sm"
          className="h-7 rounded-full text-xs gap-1"
          onClick={() => onSelect(suggestion.action)}
          disabled={disabled}
        >
          {suggestion.label}
        </Button>
      ))}
    </div>
  );
}
